import { useEffect } from 'react';

/**
 * Escape closes it.
 *
 * ON THE DOCUMENT, not on the dialog's own element. A `onKeyDown` on the
 * backdrop only fires while focus is somewhere inside it, and a dialog
 * opened by a header button that then unmounted its own focus leaves
 * `document.body` focused - so the key press goes nowhere and Escape
 * silently does nothing (v8.61.0, caught by the E2E that asserted it).
 *
 * `capture` so a nested control that stops propagation (a select being
 * dismissed, an inline editor) cannot swallow it either.
 *
 * THE KEY IS CLAIMED once it closes something (2026-10-15): prevented and
 * stopped, so the window's Escape (useKeyboardShortcuts: it cancels the
 * attack, and at a table clears the focused reaction for everyone) never
 * hears an Escape a dialog took. It used to run on, so closing a menu or a
 * modal also lost an attack. `onEscape` still gets the event.
 *
 * `showing`, asked at the key press, lets something that stays mounted while
 * hidden (the threat codex in a closed rail panel) leave the key alone then.
 */
export default function useEscapeKey(active, onEscape, showing = () => true) {
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || !showing()) return;
      e.preventDefault();
      e.stopPropagation();
      onEscape(e);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [active, onEscape, showing]);
}
